import { useEffect, useState } from 'react';
import { PRODUCT_LAUNCH_INSTANT_MS } from './product-launch';

export interface ProductLaunchCountdownState {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
    /** True once the launch instant has passed. */
    hasLaunched: boolean;
}

const SECOND_MS = 1000;
const MINUTE_MS = 60 * SECOND_MS;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/** Splits a remaining duration into whole days/hours/minutes/seconds. */
export const splitRemaining = (
    remainingMs: number,
): ProductLaunchCountdownState => {
    const remaining = Math.max(remainingMs, 0);
    return {
        days: Math.floor(remaining / DAY_MS),
        hours: Math.floor((remaining % DAY_MS) / HOUR_MS),
        minutes: Math.floor((remaining % HOUR_MS) / MINUTE_MS),
        seconds: Math.floor((remaining % MINUTE_MS) / SECOND_MS),
        hasLaunched: remainingMs <= 0,
    };
};

/**
 * Ticks once a second towards the launch instant. The first value is computed
 * during render so the server markup already carries the countdown (no empty
 * frame before hydration); the seconds digit can differ by the time the client
 * hydrates, so the rendered values are marked `suppressHydrationWarning`.
 */
export const useProductLaunchCountdown = (
    launchMs: number = PRODUCT_LAUNCH_INSTANT_MS,
): ProductLaunchCountdownState => {
    const [countdown, setCountdown] = useState(() =>
        splitRemaining(launchMs - Date.now()),
    );

    useEffect(() => {
        const tick = () => setCountdown(splitRemaining(launchMs - Date.now()));
        tick();
        const timer = window.setInterval(tick, SECOND_MS);
        return () => window.clearInterval(timer);
    }, [launchMs]);

    return countdown;
};
